/**
 * Finix Fraud Session ID Helper
 * 
 * Implements fraud detection per official Finix v2 documentation:
 * https://docs.finix.com/guides/online-payments/fraud-and-risk/fraud-detection
 * 
 * API:
 *   const FinixAuth = window.Finix.Auth(environment, merchant_id, optional_callback);
 *   const sessionKey = FinixAuth.getSessionKey();
 * 
 * FAIL-OPEN: Payment flows MUST work even if fraud session fails.
 */

// Cache FinixAuth instances per merchant to avoid redundant initialization
const authInstanceCache = new Map();

// Cache session keys
const sessionCache = new Map();

/**
 * Get Finix fraud session ID using the documented v2 API.
 * 
 * Per docs: Finix.Auth(environment, merchant_id) returns a FinixAuth object.
 * Then call FinixAuth.getSessionKey() to get the session ID.
 * 
 * FAIL-OPEN: Always resolves (never rejects). Returns null on any error.
 * 
 * @param {Object} options
 * @param {string} options.environment - 'sandbox' or 'live'
 * @param {string} options.merchantId - Finix Merchant ID (MU_xxx) - REQUIRED per docs
 * @param {number} [options.timeoutMs=3000] - Max time to wait for session ID
 * @returns {Promise<string|null>} - fraud_session_id or null
 */
export async function getFinixFraudSessionId({ environment, merchantId, timeoutMs = 3000 }) {
  const isDebug = typeof localStorage !== 'undefined' && localStorage.getItem('FSP_FRAUD_DEBUG') === '1';
  const startTime = Date.now();
  
  if (isDebug) {
    const mask = (str) => str ? `***${str.slice(-4)}` : 'null';
    console.info('[FSP_FRAUD_DEBUG] getFinixFraudSessionId called:', {
      environment,
      merchantId: mask(merchantId),
      timeoutMs
    });
  }
  
  // merchant_id is REQUIRED per Finix docs
  if (!merchantId || !environment) {
    if (isDebug) {
      console.info('[FSP_FRAUD_DEBUG] Missing merchantId or environment - returning null');
    }
    return null;
  }
  
  // Check session cache first
  const cacheKey = `${environment}:${merchantId}`;
  if (sessionCache.has(cacheKey)) {
    const cached = sessionCache.get(cacheKey);
    if (isDebug) {
      console.info('[FSP_FRAUD_DEBUG] Returning cached session:', { length: cached?.length || 0 });
    }
    return cached;
  }
  
  // Check if Finix SDK is loaded
  if (!window.Finix) {
    if (isDebug) {
      console.info('[FSP_FRAUD_DEBUG] window.Finix not available');
    }
    return null;
  }
  
  if (typeof window.Finix.Auth !== 'function') {
    if (isDebug) {
      console.info('[FSP_FRAUD_DEBUG] window.Finix.Auth is not a function');
    }
    return null;
  }
  
  return new Promise((resolve) => {
    // Timeout guard
    const timeoutId = setTimeout(() => {
      if (isDebug) {
        console.info('[FSP_FRAUD_DEBUG] Timed out after', timeoutMs, 'ms');
      }
      resolve(null);
    }, timeoutMs);
    
    const cleanup = (result) => {
      clearTimeout(timeoutId);
      const elapsed = Date.now() - startTime;
      if (isDebug) {
        console.info('[FSP_FRAUD_DEBUG] Result:', {
          success: !!result,
          length: result?.length || 0,
          elapsedMs: elapsed
        });
      }
      if (result) {
        sessionCache.set(cacheKey, result);
      }
      resolve(result);
    };
    
    try {
      if (isDebug) {
        console.info('[FSP_FRAUD_DEBUG] Calling Finix.Auth with:', {
          environment,
          merchantId: merchantId ? `***${merchantId.slice(-4)}` : null
        });
      }
      
      // Per Finix docs: Finix.Auth(environment, merchant_id, optional_callback)
      // The callback receives the sessionKey once initialized
      const finixAuth = window.Finix.Auth(
        environment,
        merchantId,
        (sessionKey) => {
          // Callback fires when session is ready
          if (isDebug) {
            console.info('[FSP_FRAUD_DEBUG] Auth callback fired:', {
              hasSessionKey: !!sessionKey,
              type: typeof sessionKey
            });
          }
          
          if (typeof sessionKey === 'string' && sessionKey.trim() !== '') {
            cleanup(sessionKey);
          } else {
            // Try getSessionKey as fallback
            try {
              const key = finixAuth?.getSessionKey?.();
              if (typeof key === 'string' && key.trim() !== '') {
                cleanup(key);
              } else {
                cleanup(null);
              }
            } catch {
              cleanup(null);
            }
          }
        }
      );
      
      // Also try getSessionKey immediately in case it's already available
      // (some implementations may have it ready synchronously)
      setTimeout(() => {
        if (sessionCache.has(cacheKey)) return; // Already resolved via callback
        
        try {
          const key = finixAuth?.getSessionKey?.();
          if (isDebug) {
            console.info('[FSP_FRAUD_DEBUG] getSessionKey() returned:', {
              hasKey: !!key,
              type: typeof key
            });
          }
          if (typeof key === 'string' && key.trim() !== '') {
            cleanup(key);
          }
        } catch (e) {
          if (isDebug) {
            console.info('[FSP_FRAUD_DEBUG] getSessionKey() error:', e?.message);
          }
        }
      }, 500);
      
    } catch (err) {
      if (isDebug) {
        console.info('[FSP_FRAUD_DEBUG] Error initializing Finix.Auth:', err?.message);
      }
      cleanup(null);
    }
  });
}

/**
 * Clear cached fraud sessions (e.g., on logout)
 */
export function clearFraudSessionCache() {
  sessionCache.clear();
  authInstanceCache.clear();
}
